import React, { useEffect, useState } from "react"
import LivescoringComponent from "@/components/Livescoring/LivescoringComponent"
import NextcaddyFullLogo from "@/svg/logos/NextcaddyFullLogo"
import FilterIcon from "@/svg/icons/FilterIcon"
import LivescoringFilter from "@/components/Livescoring/LivescoringFilter"

const LivescoringPage = ({
    competitionName,
    clubName,
    livescoringData,
    section,
    rowLimit,
    competitionId,
    teeTime,
    filteredByCalculation,
    isProduction,
    calculation,
    showToggle,
    socketChannel
}) => {
    const emptyFilters = {
        gender: null,
        handicap: {
            max: null,
            min: null
        },
        tees: [],
        rounds: []
    }
    const normalizeRoundColumns = () => {
        if (livescoringData.roundColumns && livescoringData.roundColumns.length > 0) {
            return livescoringData.roundColumns
        }

        return [{
            roundId: livescoringData.roundId,
            roundNumber: livescoringData.roundNumber,
            roundName: `J${livescoringData.roundNumber}`,
            teeTime
        }]
    }
    const roundColumns = normalizeRoundColumns()
    const defaultRoundFilters = roundColumns.map(roundColumn => roundColumn.roundId)
    const preferencesKey = "livescoring" === section ? `new-livescoring:${competitionId}:${livescoringData.roundId}:${isProduction}` : null
    const [savedView] = useState(() => {
        if (!preferencesKey) return null
        try {
            const saved = JSON.parse(window.sessionStorage.getItem(preferencesKey))
            if (!saved?.filters || !Array.isArray(saved.filters.rounds) || !Array.isArray(saved.filters.tees)) return null
            const handicap = saved.filters.handicap || {}
            return {...saved, filters: {
                gender: ["male", "female"].includes(saved.filters.gender) ? saved.filters.gender : null,
                handicap: {
                    min: Number.isFinite(handicap.min) ? handicap.min : null,
                    max: Number.isFinite(handicap.max) ? handicap.max : null
                },
                tees: saved.filters.tees.filter(value => typeof value === "number" || typeof value === "string"),
                rounds: saved.filters.rounds.filter(roundId => defaultRoundFilters.includes(roundId))
            }}
        } catch {
            return null
        }
    })

    const [calculationSelected, setCalculationSelected] = useState(
        showToggle && ["H", "S"].includes(savedView?.calculationSelected) ? savedView.calculationSelected : ("H" === calculation ? "H" : "S")
    )
    const [filterIsOpen, setFilterIsOpen] = useState(false)
    const [filters, setFilters] = useState(savedView?.filters || {...emptyFilters, rounds: defaultRoundFilters})
    const [positionCalculationKey, setPositionCalculationKey] = useState(
        typeof savedView?.positionCalculationKey === "string" && savedView.positionCalculationKey.includes(calculationSelected)
            && Object.prototype.hasOwnProperty.call(livescoringData.keysOrder, savedView.positionCalculationKey)
            ? savedView.positionCalculationKey : calculation.replace(/[HS]/, calculationSelected)
    )

    useEffect(() => {
        if (!preferencesKey) return
        try {
            window.sessionStorage.setItem(preferencesKey, JSON.stringify({filters, calculationSelected, positionCalculationKey}))
        } catch {
            // Storage can be disabled; result updates and refresh still work.
        }
    }, [preferencesKey, filters, calculationSelected, positionCalculationKey])

    const livescoringSelectedData = livescoringData

    const visibleRoundIds = filters.rounds
    const currentRoundData = visibleRoundIds.flatMap(roundId => Object.values(livescoringSelectedData.rounds[roundId] || {}))

    const playersHandicap = currentRoundData
    .flatMap(match => {
        return match.players.map(player => Number(player.playerHandicap))
    })
    .filter(Number.isFinite)

    const maxPlayerHandicap = playersHandicap.length > 0 ? Math.max(...playersHandicap) : 0
    const minPlayerHandicap = playersHandicap.length > 0 ? Math.min(...playersHandicap) : 0

    const roundNumber = livescoringData.roundNumber
    const compatibleRounds = livescoringData.compatibleRounds

    const showTotal = visibleRoundIds.length > 1 || (visibleRoundIds.length === 1 && 1 != roundNumber && compatibleRounds === true)

    const tees = currentRoundData
    .flatMap(match => match.players)
    .reduce((accumulator, player) => {
        const categoryIsIncluded = accumulator.some(item => item.categoryId === player.categoryId)

        if (!categoryIsIncluded && player.categoryId !== undefined) {
            accumulator.push({
                categoryId: player.categoryId,
                categoryName: player.categoryName,
                teeId: player.teeId,
                teeName: player.teeName,
                teeAlias: player.teeAlias
            })
        }

        return accumulator
    }, [])

    const handleCalculationChange = () => {
        setCalculationSelected(prevCalculationSelected => "H" === prevCalculationSelected ? "S" : "H")
        setPositionCalculationKey(prev =>
            prev.includes("H") ? prev.replace("H", "S").trim() : prev.replace("S", "H").trim()
        );
    }

    const handlePositionKeyChange = () => {
        setPositionCalculationKey(prev =>
            prev.includes("today") ? prev.replace("today", "").trim() : ("today"+prev).trim()
        );
    }

    const toggleFilterIsOpen = () => {
        setFilterIsOpen((prevFilterIsOpen) => !prevFilterIsOpen)
    }

    const updateFilters = (filterType, newValue) => {
        setFilters(prevFilters => {
            let value = newValue

            if ("gender" === filterType) {
                if (value === prevFilters.gender) {
                    value = null
                }
            } else if ("tees" === filterType) {
                const indexInTeesArray = prevFilters.tees.indexOf(value)

                if (-1 === indexInTeesArray) {
                    value = [...prevFilters.tees, value]
                } else {
                    value = prevFilters.tees.filter(categoryId => categoryId !== value)
                }
            } else if ("rounds" === filterType) {
                const indexInRoundsArray = prevFilters.rounds.indexOf(value)

                if (-1 === indexInRoundsArray) {
                    value = [...prevFilters.rounds, value]
                } else {
                    value = prevFilters.rounds.filter(roundId => roundId !== value)
                }
            }

            return {
                ...prevFilters,
                [filterType]: value
            }
        })
    }

    const cleanFilters = () => {
        setFilters({...emptyFilters, rounds: defaultRoundFilters})
    }

    return (
        <>
            <div className={`headerGroup ${"ecard" !== section ? "backgroundColor" : ""}`}>
                <div className="headerContent">
                    <div className="livescoring-header">
                        {
                            "ecard" !== section &&
                                <h1 className="nombreCompeticion">{ competitionName }</h1>
                        }
                    </div>
                    <div className="livescoring-subHeader">
                        <h2 className="nombreClub">{ clubName }</h2>
                    </div>
                    <div className="livescoring-calculation-selected">
                        { 'H' === calculationSelected ? 'Handicap' : 'Scratch' }
                    </div>
                </div>
                <div className="livescoring-nav-rightSide">
                    <NextcaddyFullLogo />
                    <div className="buttonZone">
                        {
                            ("livescoring" === section || "ecard" === section) &&
                                <button className="filterButton" onClick={toggleFilterIsOpen}>
                                    <FilterIcon styles={{ color: "#3387ff" }} />
                                </button>
                        }
                    </div>
                </div>
            </div>

            <div className="main">
                <div className="content livescoring-content">
                    <LivescoringComponent
                        competitionName={competitionName}
                        calculation={calculationSelected}
                        clubName={clubName}
                        livescoringData={livescoringSelectedData}
                        section={section}
                        rowLimit={rowLimit}
                        competitionId={competitionId}
                        teeTime={teeTime}
                        filteredByCalculation={filteredByCalculation}
                        isProduction={isProduction}
                        onUpdateCalculation={setCalculationSelected}
                        filters={filters}
                        positionCalculationKey={positionCalculationKey}
                        socketChannel={socketChannel}
                        roundColumns={roundColumns.filter(roundColumn => visibleRoundIds.includes(roundColumn.roundId))}
                    />
                </div>
            </div>

            <LivescoringFilter
                isOpen={filterIsOpen}
                toggleIsOpen={toggleFilterIsOpen}
                filters={filters}
                updateFilters={updateFilters}
                maxPlayerHandicap={maxPlayerHandicap}
                minPlayerHandicap={minPlayerHandicap}
                tees={tees}
                cleanFilters={cleanFilters}
                onPositionCalculationKeyChange={handlePositionKeyChange}
                onCalculationChange={handleCalculationChange}
                showTotal={showTotal}
                showToggle={showToggle}
                positionCalculationKey={positionCalculationKey}
                calculationSelected={calculationSelected}
                roundColumns={roundColumns}
            />
        </>
    )
}

export default LivescoringPage
